import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBriefcase, faCar, faChevronDown, faDownload, faFilter, faLayerGroup, faPaperPlane, faPlus, faSearch, faSyncAlt, faUserCheck, faUserTie } from '@fortawesome/free-solid-svg-icons'
import { Card, Typography, Button, Input } from "@material-tailwind/react";
import ReactSelect from 'react-select';
// summary components removed from header
import Alerty from '../common/Alerty'
import CarListing from './cars/CarListing';
import CarForm from './cars/CarForm';
import AssignAccountManagerModal from './cars/AssignAccountManagerModal';
import CarsSummaryLine from './cars/CarsSummaryLine';
import { summarizeCars } from './cars/carsSummary';
import { fetchStaffMembers, staffOptions, UNASSIGNED_MANAGER, canAssignAccountManagers } from './cars/accountManagers';
import usePermissions from '../common/usePermissions';
import { downloadCsv } from './reports/shared/reportUtils';
const { rootUrl } = require('../../config/config');


const CAR_TABS = [
    { key: 'business', label: 'Business', Icon: faBriefcase },
    { key: 'private', label: 'Private', Icon: faCar },
    { key: 'all', label: 'All', Icon: faLayerGroup }
];

const BUSINESS_TITLES = [
    { value: 'plate_no', label: 'Plate Number' },
    { value: 'insurer_expiry', label: 'Insurer Expiry' },
    { value: 'actual_bal', label: 'A/C Bal', class: 'text-right', money: true },
    { value: 'status', label: 'Status', useChip: true },
    { value: 'is_discounted', label: 'Billing', useChip: true },
    { value: 'driver_name', label: 'Driver Name' },
    { value: 'account_manager_name', label: 'Account Manager' },
    { value: 'action', label: 'Actions' }
];

// Private cars are only invoiced through job cards, so weekly balance, billing and driver columns don't apply.
const PRIVATE_TITLES = [
    { value: 'plate_no', label: 'Plate Number' },
    { value: 'owner_code', label: 'Owner' },
    { value: 'insurer_expiry', label: 'Insurer Expiry' },
    { value: 'next_government_inspection_due', label: 'Inspection Due' },
    { value: 'status', label: 'Status', useChip: true },
    { value: 'last_job_card_date', label: 'Last Job Card' },
    { value: 'account_manager_name', label: 'Account Manager' },
    { value: 'action', label: 'Actions' }
];

const ALL_TITLES = [
    { value: 'plate_no', label: 'Plate Number' },
    { value: 'insurer_expiry', label: 'Insurer Expiry' },
    { value: 'actual_bal', label: 'A/C Bal', class: 'text-right', money: true },
    { value: 'status', label: 'Status', useChip: true },
    { value: 'usage_type', label: 'Type', useChip: true },
    { value: 'is_discounted', label: 'Billing', useChip: true },
    { value: 'driver_name', label: 'Driver Name' },
    { value: 'account_manager_name', label: 'Account Manager' },
    { value: 'action', label: 'Actions' }
];

const MY_CARS = 'mine';

const EMPTY_FILTERS = {
    status: '',
    insurer: '',
    driver: '',
    owner: '',
    accountManager: '',
    balanceRange: '',
    insuranceStatus: ''
};

const TITLES_BY_TAB = { business: BUSINESS_TITLES, private: PRIVATE_TITLES, all: ALL_TITLES };

const MyCarsApp = () => {

    const { user, token } = useAuth();
    const { permissions } = usePermissions();
    const canAssignManagers = canAssignAccountManagers(permissions);
    const [cars, setCars] = useState([]);
    const [weeklyRate, setWeeklyRate] = useState(0);
    const [successMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [, setTotalAmount] = useState(0);
    const [searchBy, setSearchBy] = useState('plate_no');
    const [showAddCarForm, setShowAddCarForm] = useState(false);
    const [exportMenuOpen, setExportMenuOpen] = useState(false);
    const exportMenuRef = useRef(null);

    // summaries removed from header


    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);

    // Filter state
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [showFilters, setShowFilters] = useState(false);
    const [owners, setOwners] = useState([]);
    const [staff, setStaff] = useState([]);
    const [assignModalOpen, setAssignModalOpen] = useState(false);
    const [carStatuses, setCarStatuses] = useState([]);


    const [editingCar, setEditingCar] = useState(null);

    const [open, setOpen] = useState(false);
    const [alert, setAlert] = useState({ color: '', message: '' });

    // Archive modal state
    const [archiveModalOpen, setArchiveModalOpen] = useState(false);
    const [archivePlate, setArchivePlate] = useState('');
    const [archiveReason, setArchiveReason] = useState('');
    const [archiveLoading, setArchiveLoading] = useState(false);
    const [archiveError, setArchiveError] = useState('');

    const handleArchiveCar = (plate_no) => {
        setArchivePlate(plate_no);
        setArchiveReason('');
        setArchiveError('');
        setArchiveModalOpen(true);
    };

    const confirmArchiveCar = async () => {
        if (!archiveReason.trim()) {
            setArchiveError('Archive reason is required.');
            return;
        }
        setArchiveLoading(true);
        setArchiveError('');
        try {
            await axios.post(`${rootUrl}/cars/${archivePlate}/archive`, {
                archive_reason: archiveReason,
                archived_by: user?.username || user?.email || 'system'
            }, { headers: { Authorization: `Bearer ${token}` } });
            setArchiveModalOpen(false);
            setAlert({ color: 'green', message: `Vehicle ${archivePlate} archived successfully.` });
            setOpen(true);
            fetchCars();
        } catch (err) {
            setArchiveError(err.response?.data?.message || 'Failed to archive vehicle.');
        } finally {
            setArchiveLoading(false);
        }
    };





    const [searchParams, setSearchParams] = useSearchParams();
    const requestedTab = searchParams.get('type');
    const activeTab = TITLES_BY_TAB[requestedTab] ? requestedTab : 'business';
    const titles = TITLES_BY_TAB[activeTab];

    const handleTabChange = (tabKey) => {
        setSearchParams(prev => {
            const next = new URLSearchParams(prev);
            if (tabKey === 'business') next.delete('type'); else next.set('type', tabKey);
            return next;
        }, { replace: true });
        setCurrentPage(1);
        if (!TITLES_BY_TAB[tabKey].some(t => t.value === searchBy)) setSearchBy('plate_no');
    };

    // Helper function to format driver name
    const formatDriverName = (fullName) => {
        if (!fullName || typeof fullName !== 'string') return 'N/A';
        return fullName;


    };

    const compactSelectStyles = {
        container: (provided) => ({ ...provided, fontSize: '0.875rem' }),
        control: (provided) => ({
            ...provided,
            minHeight: '36px',
            borderRadius: '2px',
        }),
        placeholder: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
        }),
        singleValue: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
        }),
        option: (provided) => ({
            ...provided,
            fontSize: '0.875rem',
        }),
    };

    const handleEditCar = (plate_no) => {
        const carToEdit = cars.find(car => car.plate_no === plate_no);
        setEditingCar(carToEdit);
    };

    // Fetch cars on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        fetchCars();
    }, []);

    // This week's invoices (WAI) per car, for weekly coverage of the cars in view
    const [weeklyInvoices, setWeeklyInvoices] = useState(null);
    const [coverageWeek, setCoverageWeek] = useState(null);
    useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        const headers = { Authorization: `Bearer ${token}` };
        (async () => {
            try {
                const weeks = await axios.get(`${rootUrl}/weekly-summary/weeks`, { headers });
                const week = weeks.data?.[0]?.week;
                if (!week) return;
                const totals = await axios.get(`${rootUrl}/weekly-summary/${encodeURIComponent(week)}/plate-totals`, { headers, params: { transaction_types: 'WAI' } });
                if (cancelled) return;
                const byPlate = {};
                (totals.data?.data || []).forEach((row) => { byPlate[row.plate_no] = row.total_amount; });
                setWeeklyInvoices(byPlate);
                setCoverageWeek(week);
            } catch (error) {
                // Coverage is a nice-to-have; the rest of the summary still shows
                if (!cancelled) setWeeklyInvoices(null);
            }
        })();
        return () => { cancelled = true; };
    }, [token]);

    // Helper function to get insurance expiry status
    const getInsuranceStatus = (expiryDate) => {
        if (!expiryDate) return { status: 'no-data', color: 'text-gray-600 bg-gray-50 px-2 py-1 rounded-md' };

        const today = new Date();
        const expiry = new Date(expiryDate);
        const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

        if (daysUntilExpiry < 0) {
            return { status: 'expired', color: 'text-red-800 font-bold bg-red-100 px-2 py-1 rounded-md border border-red-200' };
        } else if (daysUntilExpiry <= 30) {
            return { status: 'expiring-soon', color: 'text-orange-700 font-semibold bg-orange-50 px-2 py-1 rounded-md' };
        } else {
            return { status: 'valid', color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md' };
        }
    };

    // Helper function to get balance status
    const getBalanceStatus = (balance) => {
        const bal = parseFloat(balance) || 0;
        if (bal < 0) {
            return { status: 'overpaid', color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md' };
        } else if (bal >= 0 && bal <= 1000) {
            return { status: 'excellent', color: 'text-green-700 font-semibold bg-green-50 px-2 py-1 rounded-md' };
        } else if (bal > 1000 && bal <= 3000) {
            return { status: 'low', color: 'text-blue-700 font-semibold bg-blue-50 px-2 py-1 rounded-md' };
        } else if (bal > 3000 && bal <= 5000) {
            return { status: 'midlow', color: 'text-amber-700 font-semibold bg-amber-50 px-2 py-1 rounded-md' };
        } else if (bal > 5000 && bal <= 10000) {
            return { status: 'medium', color: 'text-purple-700 font-semibold bg-purple-50 px-2 py-1 rounded-md' };
        } else if (bal > 10000 && bal <= 15000) {
            return { status: 'high', color: 'text-red-700 font-semibold bg-red-50 px-2 py-1 rounded-md' };
        } else {
            return { status: 'critical', color: 'text-red-800 font-bold bg-red-100 px-2 py-1 rounded-md border border-red-200' };
        }
    };

    // Search input handler
    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
    };

    // Filter handling functions
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setCurrentPage(1); // Reset to first page when filters change
    };

    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearchTerm('');
        setCurrentPage(1);
    };

    // Pagination handlers
    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const fetchCars = async () => {
        const token = localStorage.getItem('authToken') || 'default-token';
        try {
            // Phase 1: load cars immediately so the table renders without waiting for supplementary data
            const carResponse = await axios.get(`${rootUrl}/cars`, {
                headers: { 'Authorization': `Bearer ${token}` },
            });

            const carData = carResponse.data;
            // Use status_name already embedded in each car row (no separate status map needed)

            const mappedData = carData.map(item => ({
                plate_no: item.plate_no || 'No sender name',
                insurer: item.insurer || 'N/A',
                insurer_expiry: item.insurer_expiry ? item.insurer_expiry.split('T')[0] : 'N/A',
                year: item.year || 'no Car make',
                bal: item.bal || 0,
                actual_bal: item.actual_bal || 0,
                status: item.status_name || 'NO STATUS!',
                drive_link: item.drive_link || 'no drive link',
                driver_name: formatDriverName(item.driver_name || 'NOT ASSIGNED'),
                driver_id: item.driver_id || 'no id provided',
                input_by: item.input_by || 'sorry!',
                edit_by: item.edit_by || "N/A",
                edit_date: item.edit_date || "N/A",
                owner_code: item.owner_code || "N/A",
                account_manager_id: item.account_manager_id || null,
                account_manager_name: item.account_manager_name || null,
                last_trans_date: item.last_trans_date || null,
                discount_amount: item.discount_amount || null,
                is_discounted: item.is_discounted || false,
                usage_type: item.usage_type || 'BUSINESS',
                next_government_inspection_due: item.next_government_inspection_due || null,
                last_job_card_no: item.last_job_card_no || null,
                last_job_card_date: item.last_job_card_date || null,
                // Add these to preserve WAR metadata
                last_war_type: item.last_war_type || null,
                last_war_amount: item.last_war_amount || null,
                last_war_date: item.last_war_date || null,
                last_war_input_by: item.last_war_input_by || null,
                last_war_ref: item.last_war_ref || null,
            }));

            setCars(mappedData);
            console.log('car data', mappedData);

            const total = mappedData.reduce((acc, car) => acc + parseFloat(car.bal || 0), 0);
            setTotalAmount(total);

            // Phase 2: load supplementary filter data in the background (non-blocking)
            Promise.all([
                axios.get(`${rootUrl}/owners/list`, { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get(`${rootUrl}/cars/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
                axios.get(`${rootUrl}/system/weekly_amount`, { headers: { 'Authorization': `Bearer ${token}` } }),
            ]).then(([ownersRes, carStatusesRes, weeklyRes]) => {
                setOwners(ownersRes.data);
                setCarStatuses(carStatusesRes.data.data);
                try {
                    setWeeklyRate(Number(weeklyRes?.data?.value) || 0);
                } catch (e) { setWeeklyRate(0); }
            }).catch(err => {
                console.error('Error fetching supplementary data:', err);
            });

            fetchStaffMembers(token)
                .then(setStaff)
                .catch(err => console.error('Error fetching staff members:', err));

        } catch (error) {
            console.error("Error fetching cars: ", error);
        }
    };



    const handleDeleteCar = async (plate_no) => {
        const isConfirmed = window.confirm('Are you sure you want to delete this car?');

        if (!isConfirmed) {

            return;
        }

        try {
            await axios.delete(`${rootUrl}/cars/${plate_no}`);
            const updatedCars = cars.filter(car => car.plate_no !== plate_no);
            setCars(updatedCars);
        } catch (error) {
            console.error("Error deleting car: ", error);
        }
    };


    const handleRefreshBalances = async () => {
        try {
            console.log("Refreshing balances...");
            const response = await axios.post(`${rootUrl}/cars/refresh`);
            console.log("Balances refreshed successfully:", response);
            setAlert({ color: "green", message: response.data.message })
            setOpen(true)
            fetchCars();
        } catch (error) {
            console.error('Error refreshing balances:', error);
            setAlert({ color: "red", message: "Error refreshing balances:" })
            setOpen(true)
        }
    };

    const handleSendBalanceReport = async () => {
        try {
            console.log("Sending car balance report...");
            const token = localStorage.getItem('authToken') || 'default-token';
            const response = await axios.post(`${rootUrl}/cars/balance-report`, {}, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            console.log("Balance report queued successfully:", response);
            setAlert({
                color: "green",
                message: `Balance report queued successfully! Job ID: ${response.data.jobId}`
            });
            setOpen(true);
        } catch (error) {
            console.error('Error sending balance report:', error);
            setAlert({
                color: "red",
                message: error.response?.data?.error || "Error sending balance report"
            });
            setOpen(true);
        }
    };

    // Enhanced summary calculations
    // eslint-disable-next-line no-unused-vars
    const summaryStats = useMemo(() => {
        const totalCars = cars.length;
        const activeCars = cars.filter(car => car.status === 'Active').length;
        const inactiveCars = cars.filter(car => car.status === 'Inactive').length;
        // Group cars by their status dynamically (e.g., Active, Inactive, Pending, etc.)
        const statusCounts = cars.reduce((acc, car) => {
            const s = car.status || 'Unknown';
            acc[s] = (acc[s] || 0) + 1;
            return acc;
        }, {});

        const totalBalance = cars.reduce((sum, car) => sum + parseFloat(car.actual_bal || 0), 0);
        const excellentBalanceCars = cars.filter(car => {
            const bal = parseFloat(car.actual_bal || 0);
            return bal >= 0 && bal <= 1000;
        }).length;
        const lowBalanceCars = cars.filter(car => {
            const bal = parseFloat(car.actual_bal || 0);
            return bal > 1000 && bal <= 3000;
        }).length;
        const midlowBalanceCars = cars.filter(car => {
            const bal = parseFloat(car.actual_bal || 0);
            return bal > 3000 && bal <= 5000;
        }).length;
        const mediumBalanceCars = cars.filter(car => {
            const bal = parseFloat(car.actual_bal || 0);
            return bal > 5000 && bal <= 10000;
        }).length;
        // Dynamic balance classification based on system weekly amount
        const dailyRate = (Number(weeklyRate) || 0) / 6;

        // Compute counts and total balances per band in a single pass
        const categories = cars.reduce((acc, car) => {
            const bal = parseFloat(car.actual_bal || 0);
            if (bal <= dailyRate) {
                acc.good.count += 1;
                acc.good.sum += bal;
            } else if (bal <= 2 * dailyRate) {
                acc.warning.count += 1;
                acc.warning.sum += bal;
            } else if (bal <= 3 * dailyRate) {
                acc.notice.count += 1;
                acc.notice.sum += bal;
            } else if (bal <= 6 * dailyRate) {
                acc.high.count += 1;
                acc.high.sum += bal;
            } else if (bal <= 8 * dailyRate) {
                acc.critical.count += 1;
                acc.critical.sum += bal;
            } else {
                acc.fatal.count += 1;
                acc.fatal.sum += bal;
            }
            return acc;
        }, {
            good: { count: 0, sum: 0 },
            warning: { count: 0, sum: 0 },
            notice: { count: 0, sum: 0 },
            high: { count: 0, sum: 0 },
            critical: { count: 0, sum: 0 },
            fatal: { count: 0, sum: 0 }
        });

        const good = categories.good.count;
        const warning = categories.warning.count;
        const notice = categories.notice.count;
        const high = categories.high.count;
        const critical = categories.critical.count;
        const fatal = categories.fatal.count;

        const goodTotal = categories.good.sum;
        const warningTotal = categories.warning.sum;
        const noticeTotal = categories.notice.sum;
        const highTotal = categories.high.sum;
        const criticalTotal = categories.critical.sum;
        const fatalTotal = categories.fatal.sum;

        // total cars above 'high' threshold
        const highBalanceCars = high + critical + fatal;
        const criticalBalanceCars = critical + fatal;

        const expiringInsurance = cars.filter(car => {
            if (!car.insurer_expiry || car.insurer_expiry === 'N/A') return false;
            const expiry = new Date(car.insurer_expiry);
            const today = new Date();
            const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));
            return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
        }).length;

        const expiredInsurance = cars.filter(car => {
            if (!car.insurer_expiry || car.insurer_expiry === 'N/A') return false;
            const expiry = new Date(car.insurer_expiry);
            const today = new Date();
            return expiry < today;
        }).length;

        const assignedDrivers = cars.filter(car =>
            car.driver_name &&
            car.driver_name !== 'N/A' &&
            car.driver_name !== 'NOT ASSIGNED'
        ).length;
        const unassignedCars = cars.filter(car =>
            !car.driver_name ||
            car.driver_name === 'N/A' ||
            car.driver_name === 'NOT ASSIGNED'
        ).length;

        return {
            totalCars,
            // counts grouped by status (object with keys = status names)
            statusCounts,
            activeCars,
            inactiveCars,
            totalBalance,
            excellentBalanceCars,
            lowBalanceCars,
            midlowBalanceCars,
            mediumBalanceCars,
            highBalanceCars,
            criticalBalanceCars,
            balanceCategories: {
                good,
                goodTotal,
                warning,
                warningTotal,
                notice,
                noticeTotal,
                high,
                highTotal,
                critical,
                criticalTotal,
                fatal,
                fatalTotal,
                dailyRate
            },
            expiringInsurance,
            expiredInsurance,
            assignedDrivers,
            unassignedCars
        };
    }, [cars, weeklyRate]);

    // Enhanced filtering with multiple criteria
    const tabCounts = useMemo(() => {
        const privateCount = cars.filter(car => car.usage_type === 'PRIVATE').length;
        return { business: cars.length - privateCount, private: privateCount, all: cars.length };
    }, [cars]);

    const filteredCars = useMemo(() => {
        let filtered = cars;

        // Business / private tab
        if (activeTab === 'business') {
            filtered = filtered.filter(car => car.usage_type !== 'PRIVATE');
        } else if (activeTab === 'private') {
            filtered = filtered.filter(car => car.usage_type === 'PRIVATE');
        }

        // Text search
        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(car => {
                switch (searchBy) {
                    case 'plate_no':
                        return car.plate_no.toLowerCase().includes(lowerCaseSearchTerm);
                    case 'insurer':
                        return car.insurer.toLowerCase().includes(lowerCaseSearchTerm);
                    case 'year':
                        return car.year.toString().toLowerCase().includes(lowerCaseSearchTerm);
                    case 'status':
                        return car.status.toLowerCase().includes(lowerCaseSearchTerm);
                    case 'driver_name':
                        return car.driver_name.toLowerCase().includes(lowerCaseSearchTerm);
                    case 'owner_code':
                        return car.owner_code.toLowerCase().includes(lowerCaseSearchTerm);
                    case 'account_manager_name':
                        return (car.account_manager_name || 'unassigned').toLowerCase().includes(lowerCaseSearchTerm);
                    default:
                        return false;
                }
            });
        }

        // Status filter
        if (filters.status) {
            filtered = filtered.filter(car => car.status === filters.status);
        }

        // Insurer filter
        if (filters.insurer) {
            filtered = filtered.filter(car => car.insurer === filters.insurer);
        }

        // Driver filter
        if (filters.driver) {
            filtered = filtered.filter(car => car.driver_name === filters.driver);
        }

        // Owner filter
        if (filters.owner) {
            filtered = filtered.filter(car => car.owner_code === filters.owner);
        }

        // Account manager filter
        if (filters.accountManager === MY_CARS) {
            filtered = filtered.filter(car => car.account_manager_id !== null && String(car.account_manager_id) === String(user?.id));
        } else if (filters.accountManager === UNASSIGNED_MANAGER) {
            filtered = filtered.filter(car => !car.account_manager_id);
        } else if (filters.accountManager) {
            filtered = filtered.filter(car => String(car.account_manager_id) === String(filters.accountManager));
        }


        // Balance range filter
        if (filters.balanceRange) {
            filtered = filtered.filter(car => {
                const balance = parseFloat(car.actual_bal || 0);
                switch (filters.balanceRange) {
                    case 'excellent':
                        return balance >= 0 && balance <= 1000;
                    case 'low':
                        return balance > 1000 && balance <= 3000;
                    case 'midlow':
                        return balance > 3000 && balance <= 5000;
                    case 'medium':
                        return balance > 5000 && balance <= 10000;
                    case 'high':
                        return balance > 10000 && balance <= 15000;
                    case 'critical':
                        return balance > 15000;
                    default:
                        return true;
                }
            });
        }

        // Insurance status filter
        if (filters.insuranceStatus) {
            filtered = filtered.filter(car => {
                if (!car.insurer_expiry || car.insurer_expiry === 'N/A') return false;
                const expiry = new Date(car.insurer_expiry);
                const today = new Date();
                const daysUntilExpiry = Math.ceil((expiry - today) / (1000 * 60 * 60 * 24));

                switch (filters.insuranceStatus) {
                    case 'valid':
                        return daysUntilExpiry > 30;
                    case 'expiring':
                        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
                    case 'expiring-week':
                        return daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
                    case 'expired':
                        return daysUntilExpiry < 0;
                    default:
                        return true;
                }
            });
        }

        return filtered;
    }, [cars, searchTerm, searchBy, filters, activeTab, user?.id]);

    const unassignedCount = useMemo(() => cars.filter(car => !car.account_manager_id).length, [cars]);

    const accountManagerFilterOptions = useMemo(() => [
        { value: MY_CARS, label: 'My cars' },
        { value: UNASSIGNED_MANAGER, label: 'Unassigned' },
        ...staffOptions(staff).map(o => ({ value: String(o.value), label: o.label }))
    ], [staff]);

    const toggleMyCars = () => {
        handleFilterChange('accountManager', filters.accountManager === MY_CARS ? '' : MY_CARS);
    };

    const handleAssigned = (message) => {
        setAssignModalOpen(false);
        setAlert({ color: 'green', message });
        setOpen(true);
        fetchCars();
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
                setExportMenuOpen(false);
            }
        };

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setExportMenuOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('keydown', handleEscape);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('keydown', handleEscape);
        };
    }, []);

    const exportCarsToCsv = useCallback((sourceCars, scopeLabel) => {
        if (!sourceCars || sourceCars.length === 0) return;

        const headers = [
            'Plate No.',
            'Insurer',
            'Insurance Expiry',
            'Year',
            'Balance',
            'Status',
            'Type',
            'Billing',
            'Driver Name',
            'Owner Code',
            'Account Manager',
            'Last Transaction Date',
            'Last Job Card',
            'Last Job Card Date'
        ];

        const rows = sourceCars.map((car) => [
            car.plate_no || '',
            car.insurer || '',
            car.insurer_expiry || '',
            car.year || '',
            Number(car.actual_bal || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }),
            car.status || '',
            car.usage_type === 'PRIVATE' ? 'Private' : 'Business',
            (car.discount_amount || car.is_discounted) ? 'Discounted' : 'Billable',
            car.driver_name || '',
            car.owner_code || '',
            car.account_manager_name || 'Unassigned',
            car.last_trans_date ? dayjs(car.last_trans_date).format('YYYY-MM-DD') : '',
            car.last_job_card_no || '',
            car.last_job_card_date ? dayjs(car.last_job_card_date).format('YYYY-MM-DD') : ''
        ]);

        const filename = `cars_${scopeLabel}_${dayjs().format('YYYY-MM-DD')}.csv`;
        downloadCsv(headers, rows, filename);
    }, []);

    const handleExportFilteredCars = useCallback(() => {
        exportCarsToCsv(filteredCars, activeTab === 'all' ? 'filtered' : `${activeTab}_filtered`);
        setExportMenuOpen(false);
    }, [exportCarsToCsv, filteredCars, activeTab]);

    const handleExportAllCars = useCallback(() => {
        exportCarsToCsv(cars, 'all');
        setExportMenuOpen(false);
    }, [cars, exportCarsToCsv]);

    // Pagination
    const viewSummary = useMemo(() => summarizeCars(filteredCars, new Date(), weeklyInvoices), [filteredCars, weeklyInvoices]);

    const paginatedCars = useMemo(() => {
        const startIndex = (currentPage - 1) * pageSize;
        const endIndex = startIndex + pageSize;
        return filteredCars.slice(startIndex, endIndex);
    }, [filteredCars, currentPage, pageSize]);

    // Update total pages
    useEffect(() => {
        setTotalPages(Math.ceil(filteredCars.length / pageSize));
        if (currentPage > Math.ceil(filteredCars.length / pageSize)) {
            setCurrentPage(1);
        }
    }, [filteredCars.length, pageSize, currentPage]);


    return (
        <div className="p-3 sm:p-6 bg-[#fff8f5] min-h-screen">
            <div className="w-full">
                <div className="text-center mb-6">
                    <Typography variant="h3" className="text-[#2a2e30] font-bold text-3xl sm:text-4xl mb-2">
                        Cars Onboard
                    </Typography>
                    <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
                </div>
                <Alerty
                    open={open}
                    setOpen={setOpen}
                    color={alert.color}
                    message={alert.message}
                />

                {successMessage && (
                    <div className="bg-brand-success/20 text-brand-success border border-brand-success/30 text-center p-3 rounded-lg mb-4">
                        {successMessage}
                    </div>
                )}

                {/* Business / Private / All tabs */}
                <div className="flex border-b border-gray-200 mb-6" role="tablist">
                    {CAR_TABS.map(({ key, label, Icon }) => {
                        const isActive = activeTab === key;
                        return (
                            <button
                                key={key}
                                type="button"
                                role="tab"
                                aria-selected={isActive}
                                aria-label={label}
                                onClick={() => handleTabChange(key)}
                                className={`flex items-center gap-2 px-4 py-2 -mb-px text-sm font-semibold border-b-2 transition-colors ${
                                    isActive
                                        ? 'border-[#FF5733] text-[#FF5733]'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <span className="md:hidden"><FontAwesomeIcon icon={Icon} /></span>
                                <span className="hidden md:inline">{label}</span>
                                <span className={`text-xs rounded-full px-2 py-0.5 ${isActive ? 'bg-orange-100 text-[#FF5733]' : 'bg-gray-100 text-gray-500'}`}>
                                    {tabCounts[key]}
                                </span>
                            </button>
                        );
                    })}
                </div>

                {/* Combined Filter and Table Container */}
                <div className="mb-4 sm:mb-8">
                    <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-3 sm:gap-4 mb-4 sm:mb-6 bg-white border border-gray-200 rounded-sm p-2 sm:p-3 shadow-none">
                        <div className="flex flex-wrap gap-2 sm:justify-start justify-end w-full sm:w-auto">
                            <Button
                                onClick={() => setShowFilters(!showFilters)}
                                variant="outlined"
                                size="sm"
                                className="flex items-center gap-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-3 py-2 shadow-none border border-slate-200 normal-case"
                            >
                                <FontAwesomeIcon icon={faFilter} />
                                {showFilters ? 'Hide Filters' : 'Filters'}
                            </Button>
                            <Button
                                onClick={toggleMyCars}
                                variant="outlined"
                                size="sm"
                                aria-pressed={filters.accountManager === MY_CARS}
                                className={`flex items-center gap-2 text-sm rounded-sm px-3 py-2 shadow-none border normal-case ${filters.accountManager === MY_CARS
                                    ? 'bg-[#fff1ec] border-[#FF5733] text-[#FF5733]'
                                    : 'bg-slate-100 hover:bg-slate-200 text-slate-700 border-slate-200'}`}
                            >
                                <FontAwesomeIcon icon={faUserCheck} />
                                My Cars
                            </Button>
                            {canAssignManagers && (
                                <Button
                                    onClick={() => setAssignModalOpen(true)}
                                    variant="outlined"
                                    size="sm"
                                    disabled={!cars.length}
                                    className="flex items-center gap-2 border-[#FF5733] text-[#FF5733] hover:bg-[#fff4ef] text-sm font-semibold rounded-sm px-3 py-2 shadow-none normal-case disabled:opacity-50"
                                >
                                    <FontAwesomeIcon icon={faUserTie} />
                                    Assign Manager
                                </Button>
                            )}
                            <Button
                                onClick={handleRefreshBalances}
                                className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faSyncAlt} />
                                <span>Refresh</span>
                            </Button>
                            <Button
                                onClick={handleSendBalanceReport}
                                className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faPaperPlane} />
                                <span>Send Report</span>
                            </Button>
                            <div className="relative" ref={exportMenuRef}>
                                <Button
                                    onClick={() => setExportMenuOpen((prev) => !prev)}
                                    variant="outlined"
                                    size="sm"
                                    disabled={!cars.length}
                                    className="border-[#FF5733] text-[#FF5733] hover:bg-[#fff4ef] text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case disabled:opacity-50 disabled:pointer-events-none"
                                >
                                    <FontAwesomeIcon icon={faDownload} />
                                    <span>Export CSV</span>
                                    <FontAwesomeIcon icon={faChevronDown} className="text-xs" />
                                </Button>
                                {exportMenuOpen && (
                                    <div className="absolute right-0 z-20 mt-2 w-52 overflow-hidden rounded-sm border border-slate-200 bg-white shadow-lg">
                                        <button
                                            type="button"
                                            onClick={handleExportFilteredCars}
                                            disabled={!filteredCars.length}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="text-xs" />
                                            Export filtered CSV
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleExportAllCars}
                                            disabled={!cars.length}
                                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:text-slate-300"
                                        >
                                            <FontAwesomeIcon icon={faDownload} className="text-xs" />
                                            Export all CSV
                                        </button>
                                    </div>
                                )}
                            </div>
                            <Button
                                onClick={() => setShowAddCarForm(true)}
                                className="bg-brand-success hover:bg-brand-success/90 text-white text-sm font-semibold rounded-sm px-3 py-2 shadow-none flex items-center gap-2 normal-case"
                                size="sm"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <span>New Car</span>
                            </Button>
                        </div>

                        <div className="flex items-center gap-2 sm:justify-end justify-end w-full sm:w-auto">
                            <Typography variant="small" color="blue-gray" className="hidden sm:inline text-xs font-semibold uppercase tracking-wide">
                                Per page
                            </Typography>
                            <select
                                value={pageSize}
                                onChange={(e) => setPageSize(parseInt(e.target.value, 10))}
                                className="w-16 h-9 px-2 text-sm border border-slate-200 rounded-sm bg-white text-slate-700 focus:outline-none focus:border-[#FF5733] focus:ring-1 focus:ring-[#FF5733]"
                            >
                                <option value={5}>5</option>
                                <option value={10}>10</option>
                                <option value={25}>25</option>
                                <option value={50}>50</option>
                            </select>
                        </div>
                    </div>
                </div>

                <Card className="bg-white shadow-lg rounded-xl border border-gray-100 p-4 sm:p-6">

                    {/* Search Bar */}
                    <div className="flex flex-col md:flex-row flex-wrap gap-3 mb-4">
                        <div className="flex flex-col md:flex-row gap-2 flex-1 min-w-0 items-stretch md:items-center">
                            <div className="flex-shrink-0 w-full md:w-40">
                                <ReactSelect
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    options={titles.map(t => ({ value: t.value, label: t.label }))}
                                    value={titles.map(t => ({ value: t.value, label: t.label })).find(o => o.value === searchBy)}
                                    onChange={(option) => setSearchBy(option?.value || 'plate_no')}
                                    isSearchable={false}
                                    styles={compactSelectStyles}
                                />
                            </div>
                            <Input
                                placeholder={`Search ${titles.find(t => t.value === searchBy)?.label || ''}`}
                                value={searchTerm}
                                onChange={handleSearchChange}
                                icon={<FontAwesomeIcon icon={faSearch} className="text-brand-primary" />}
                                className="flex-1 text-sm w-full"
                                containerProps={{
                                    className: "flex-1 min-w-0 focus-within:ring-2 focus-within:ring-brand-primary/20 rounded-sm"
                                }}
                            />
                        </div>
                    </div>

                    <CarsSummaryLine summary={viewSummary} showBalance={activeTab !== 'private'} coverageWeek={coverageWeek} />

                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 gap-3">
                        <Typography variant="small" color="blue-gray" className="font-medium text-sm">
                            Showing {paginatedCars.length} of {filteredCars.length} cars
                        </Typography>
                        {unassignedCount > 0 && (
                            <button
                                type="button"
                                onClick={() => handleFilterChange('accountManager', UNASSIGNED_MANAGER)}
                                className="inline-flex items-center gap-2 rounded-sm border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                            >
                                <FontAwesomeIcon icon={faUserTie} />
                                {unassignedCount} car{unassignedCount === 1 ? '' : 's'} without an account manager
                            </button>
                        )}
                    </div>

                    {/* Advanced Filters */}
                    {showFilters && (
                        <Card className="p-4 sm:p-6 mb-4 sm:mb-6 bg-white shadow-none rounded-xl border border-gray-100">
                            <Typography variant="h6" className="mb-4 text-[#2a2e30] font-semibold text-sm">Filters</Typography>
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                                <div>
                                    <ReactSelect
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        options={carStatuses.map(s => ({ value: s.name, label: s.name }))}
                                        value={carStatuses.map(s => ({ value: s.name, label: s.name })).find(o => o.value === filters.status) || null}
                                        onChange={(option) => handleFilterChange('status', option ? option.value : '')}
                                        isClearable
                                        placeholder="All Status"
                                        styles={compactSelectStyles}
                                    />
                                </div>

                                <div>
                                    <ReactSelect
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        options={owners.map(o => ({ value: o.code, label: o.name }))}
                                        value={owners.map(o => ({ value: o.code, label: o.name })).find(o => o.value === filters.owner) || null}
                                        onChange={(option) => handleFilterChange('owner', option ? option.value : '')}
                                        isClearable
                                        placeholder="All Owners"
                                        styles={compactSelectStyles}
                                    />
                                </div>

                                <div>
                                    <ReactSelect
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        aria-label="Account Manager"
                                        options={accountManagerFilterOptions}
                                        value={accountManagerFilterOptions.find(o => o.value === filters.accountManager) || null}
                                        onChange={(option) => handleFilterChange('accountManager', option ? option.value : '')}
                                        isClearable
                                        placeholder="All Account Managers"
                                        styles={compactSelectStyles}
                                    />
                                </div>

                                <div>
                                    <ReactSelect
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        options={[
                                            { value: 'excellent', label: 'Excellent (KES 0 - 1,000)' },
                                            { value: 'low', label: 'Low (KES 1,001 - 3,000)' },
                                            { value: 'midlow', label: 'Mid-Low (KES 3,001 - 5,000)' },
                                            { value: 'medium', label: 'Medium (KES 5,001 - 10,000)' },
                                            { value: 'high', label: 'High (KES 10,001 - 15,000)' },
                                            { value: 'critical', label: 'Critical (> KES 15,000)' },
                                        ]}
                                        value={[
                                            { value: 'excellent', label: 'Excellent (KES 0 - 1,000)' },
                                            { value: 'low', label: 'Low (KES 1,001 - 3,000)' },
                                            { value: 'midlow', label: 'Mid-Low (KES 3,001 - 5,000)' },
                                            { value: 'medium', label: 'Medium (KES 5,001 - 10,000)' },
                                            { value: 'high', label: 'High (KES 10,001 - 15,000)' },
                                            { value: 'critical', label: 'Critical (> KES 15,000)' },
                                        ].find(o => o.value === filters.balanceRange) || null}
                                        onChange={(option) => handleFilterChange('balanceRange', option ? option.value : '')}
                                        isClearable
                                        placeholder="All Balances"
                                        styles={compactSelectStyles}
                                    />
                                </div>

                                <div>
                                    <ReactSelect
                                        className="react-select-container"
                                        classNamePrefix="react-select"
                                        options={[
                                            { value: 'valid', label: 'Valid' },
                                            { value: 'expiring', label: 'Expiring Soon (≤ 30 days)' },
                                            { value: 'expiring-week', label: 'Expiring in One Week or Less (≤ 7 days)' },
                                            { value: 'expired', label: 'Expired' }
                                        ]}
                                        value={[
                                            { value: 'valid', label: 'Valid' },
                                            { value: 'expiring', label: 'Expiring Soon (≤ 30 days)' },
                                            { value: 'expiring-week', label: 'Expiring in One Week or Less (≤ 7 days)' },
                                            { value: 'expired', label: 'Expired' }
                                        ].find(o => o.value === filters.insuranceStatus) || null}
                                        onChange={(option) => handleFilterChange('insuranceStatus', option ? option.value : '')}
                                        isClearable
                                        placeholder="All Insurance"
                                        styles={compactSelectStyles}
                                    />
                                </div>
                            </div>
                            <div className="flex justify-end">
                                <Button
                                    onClick={clearFilters}
                                    variant="outlined"
                                    size="sm"
                                    className="flex items-center gap-2 border-gray-400 text-gray-600 hover:bg-gray-50 transition-colors duration-200"
                                >
                                    Clear All Filters
                                </Button>
                            </div>
                        </Card>
                    )}

                    {/* Table for all screen sizes (wrapped to prevent page overflow on mobile) */}
                    <div className="w-full overflow-x-auto box-border">
                        <div className="min-w-0 max-w-full">
                            <div className="w-full min-w-0">
                                <CarListing
                                    filteredCars={paginatedCars}
                                    titles={titles}
                                    handleDeleteCar={handleDeleteCar}
                                    handleEditCar={handleEditCar}
                                    handleArchiveCar={handleArchiveCar}
                                    getInsuranceStatus={getInsuranceStatus}
                                    getBalanceStatus={getBalanceStatus}
                                />
                            </div>
                        </div>
                    </div>
                </Card>

                <AssignAccountManagerModal
                    isOpen={assignModalOpen}
                    onClose={() => setAssignModalOpen(false)}
                    onAssigned={handleAssigned}
                    token={token}
                    staff={staff}
                    cars={cars}
                    filteredCars={filteredCars}
                />

                {/* Pagination Controls */}
                {totalPages > 1 && (
                    <Card className="bg-white rounded-xl shadow-lg p-4 mt-6 border border-gray-100">
                        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                            <div className="w-full sm:w-auto flex items-center justify-between sm:justify-start gap-2">
                                <Typography variant="small" color="blue-gray" className="text-brand-secondary">
                                    Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, filteredCars.length)} of {filteredCars.length} cars
                                </Typography>

                                {/* Compact mobile page indicator */}
                                <div className="sm:hidden ml-2">
                                    <span className="text-sm text-brand-secondary">Page {currentPage}/{totalPages}</span>
                                </div>
                            </div>

                            {/* Desktop controls */}
                            <div className="hidden sm:flex items-center gap-4">
                                <div className="flex items-center gap-2">
                                    <Typography variant="small" color="blue-gray" className="text-brand-secondary">Cars per page:</Typography>
                                    <div className="w-20">
                                        <ReactSelect
                                            className="react-select-container"
                                            classNamePrefix="react-select"
                                            options={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }]}
                                            value={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }].find(o => o.value === pageSize)}
                                            onChange={(option) => setPageSize(option ? option.value : 10)}
                                            isSearchable={false}
                                        />
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => goToPage(currentPage - 1)}
                                        disabled={currentPage === 1}
                                        className="flex items-center gap-1 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent transition-colors duration-200"
                                    >
                                        Previous
                                    </Button>

                                    <div className="flex gap-1">
                                        {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                                            let pageNumber;
                                            if (totalPages <= 5) {
                                                pageNumber = i + 1;
                                            } else if (currentPage <= 3) {
                                                pageNumber = i + 1;
                                            } else if (currentPage >= totalPages - 2) {
                                                pageNumber = totalPages - 4 + i;
                                            } else {
                                                pageNumber = currentPage - 2 + i;
                                            }

                                            return (
                                                <Button
                                                    key={pageNumber}
                                                    variant={currentPage === pageNumber ? "filled" : "outlined"}
                                                    size="sm"
                                                    onClick={() => goToPage(pageNumber)}
                                                    className={`min-w-[2rem] px-2 ${currentPage === pageNumber
                                                        ? 'bg-brand-primary hover:bg-brand-primary/90'
                                                        : 'border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white'
                                                        } transition-colors duration-200`}
                                                >
                                                    {pageNumber}
                                                </Button>
                                            );
                                        })}
                                    </div>

                                    <Button
                                        variant="outlined"
                                        size="sm"
                                        onClick={() => goToPage(currentPage + 1)}
                                        disabled={currentPage === totalPages}
                                        className="flex items-center gap-1 border-brand-primary text-brand-primary hover:bg-brand-primary hover:text-white disabled:border-gray-300 disabled:text-gray-400 disabled:hover:bg-transparent transition-colors duration-200"
                                    >
                                        Next
                                    </Button>
                                </div>
                            </div>

                            {/* Mobile controls: stacked and compact */}
                            <div className="flex sm:hidden w-full flex-col gap-3 mt-2">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <Typography variant="small" color="blue-gray" className="text-brand-secondary">Cars per page:</Typography>
                                        <div className="w-24">
                                            <ReactSelect
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                                options={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }]}
                                                value={[{ value: 5, label: '5' }, { value: 10, label: '10' }, { value: 25, label: '25' }, { value: 50, label: '50' }].find(o => o.value === pageSize)}
                                                onChange={(option) => setPageSize(option ? option.value : 10)}
                                                isSearchable={false}
                                            />
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => goToPage(currentPage - 1)}
                                            disabled={currentPage === 1}
                                            className="px-3 py-1"
                                        >
                                            Prev
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            size="sm"
                                            onClick={() => goToPage(currentPage + 1)}
                                            disabled={currentPage === totalPages}
                                            className="px-3 py-1"
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>

                                <div className="flex items-center justify-center">
                                    <span className="text-sm text-brand-secondary">Page {currentPage} of {totalPages}</span>
                                </div>
                            </div>
                        </div>
                    </Card>
                )}

            </div>

            {(showAddCarForm || editingCar) && (
                <CarForm setShowAddCarForm={setShowAddCarForm} editingCar={editingCar} setEditingCar={setEditingCar} fetchCars={fetchCars} />
            )}

            {/* Archive Car Modal */}
            {archiveModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
                    <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md mx-4">
                        <h2 className="text-xl font-bold text-[#2a2e30] mb-1">Archive Vehicle</h2>
                        <p className="text-sm text-gray-500 mb-4">
                            Archiving <strong>{archivePlate}</strong> will hide it from the active fleet and block new transactions and driver assignments.
                        </p>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">
                            Reason for archiving <span className="text-red-500">*</span>
                        </label>
                        <textarea
                            className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5733] resize-none"
                            rows={3}
                            placeholder="e.g. Vehicle sold, written off, decommissioned..."
                            value={archiveReason}
                            onChange={(e) => setArchiveReason(e.target.value)}
                        />
                        {archiveError && (
                            <p className="text-red-500 text-sm mt-1">{archiveError}</p>
                        )}
                        <div className="flex justify-end gap-3 mt-4">
                            <button
                                onClick={() => setArchiveModalOpen(false)}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm rounded-sm px-4 py-2"
                                disabled={archiveLoading}
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmArchiveCar}
                                className="bg-[#FF5733] hover:bg-[#e64520] text-white text-sm font-semibold rounded-sm px-4 py-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                disabled={archiveLoading}
                            >
                                {archiveLoading ? 'Archiving...' : 'Archive Vehicle'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
export default MyCarsApp;
